import { ApplicationRef, Component, EventEmitter, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { ProductsService } from '../../../../../../../shared/products/products.service';
import { ProductCategory } from '../../../../../../../shared/products/models/ProductCategory';
import { bootstrapPlusLg, bootstrapTrash } from '@ng-icons/bootstrap-icons';
import { NgClass } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import { Job } from '../../../../../../../shared/Jobbing/models/Job';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { wholeNumberValidator } from '../../../../../../../shared/validators/whole-number.validator';
import { atLeastOneRequiredValidator } from '../../../../../../../shared/validators/at-least-one-required.validator';

@Component({
  selector: 'app-product-information-form',
  standalone: true,
  imports: [ReactiveFormsModule, NgSelectModule, NgClass, NgIcon],
  templateUrl: './product-information-form.component.html',
  styleUrl: './product-information-form.component.scss',
  viewProviders: [
    provideIcons({
      bootstrapPlusLg,
      bootstrapTrash,
    })
  ]
})
export class ProductInformationFormComponent implements OnInit, OnChanges {

  @Input()
  formGroup!: FormGroup;

  @Input()
  startingProducts: any[] | null = null;

  @Input()
  resetEmitter!: EventEmitter<boolean>;

  @Input()
  defaultEmitter!: EventEmitter<{ freightPayer: number, isTimeBased: boolean, products: any[] }>;

  @Input()
  updateEmitter: EventEmitter<Job> | null = null;

  productCategories: ProductCategory[] = [];
  // used to memo the product descriptions for each product category
  productDescriptions: any = {};

  lockdown: any;
  dangerousProductCodes: any;

  isTimeBased: boolean = false;

  constructor(private readonly fb: FormBuilder, private readonly productService: ProductsService, private readonly appRef: ApplicationRef) { }

  ngOnInit(): void {
    this.resetEmitter.subscribe(a => {
      if (a) {
        (<FormArray>this.formGroup.get('productInformation.products')).clear();
        this.appRef.tick();
        this.setDefaultProductList();
      }
    })

    this.defaultEmitter.subscribe(async (defaultProductValues) => {
      await this.reloadProductCategories(defaultProductValues.freightPayer, defaultProductValues.isTimeBased);

      (<FormArray>this.formGroup.get('productInformation.products')).clear();

      if (defaultProductValues.products === null) {
        this.formGroup.get('productInformation.products')?.patchValue([]);

        this.appRef.tick();
        this.addProduct();
        return;
      }

      this.appRef.tick();

      defaultProductValues.products.forEach((product: any) => {
        const productLine = this.fb.group({
          jobProductId: this.fb.control(product.jobProductId),
          category: this.fb.control(product.productCategory, [Validators.required]),
          items: this.fb.array([])
        });

        product.productItems.forEach((item: any) => {
          (<FormArray>productLine.get('items'))?.push(this.fb.group({
            jobProductItemId: this.fb.control(item.jobProductItemId),
            description: this.fb.control(item.itemDescription, [Validators.required]),
            quantity: this.fb.control(item.quantity, [atLeastOneRequiredValidator(['quantity', 'weight', 'volumeM3']), Validators.min(1), wholeNumberValidator()]),
            weight: this.fb.control(item.weight, [atLeastOneRequiredValidator(['quantity', 'weight', 'volumeM3']), Validators.min(0)]),
            volumeM3: this.fb.control(item.volume, [atLeastOneRequiredValidator(['quantity', 'weight', 'volumeM3']), Validators.min(0)]),
            dangerousGoodsClass: this.fb.control(item.dangerousGoodsClass),
          }));
        });

        (<FormArray>this.formGroup.get('productInformation.products')).push(productLine);
      });
    });

    this.initializeForm();

    const freightPayerControl = this.formGroup.get('jobInformation')?.get('freightPayer');
    if (!freightPayerControl?.value) {
      this.setLockdown(true);
    }

    freightPayerControl?.valueChanges.subscribe(async (value) => {
      this.setLockdown(false);
      const isTimeBased = this.formGroup.get('jobInformation')?.get('timeBased')?.value;
      await this.reloadProductCategories(value, isTimeBased);
    });

    this.formGroup.get('jobInformation')?.get('timeBased')?.valueChanges.subscribe(async (value) => {
      const clienteleId = this.formGroup.get('jobInformation')?.get('freightPayer')?.value;
      this.isTimeBased = value;
      await this.reloadProductCategories(clienteleId, value);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['startingProducts']?.currentValue !== changes['startingProducts']?.previousValue) {
      this.getProductList().clear();
      this.initializeForm();
    }
  }

  setLockdown(state: boolean) {
    this.lockdown = state;
    if (state) {
      this.getProductList().controls.forEach((control, index) => {
        control.get('category')?.disable({ emitEvent: false });
        this.getProductItems(index).controls.forEach(itemControl => {
          itemControl.get('description')?.disable({ emitEvent: false });
          itemControl.get('quantity')?.disable({ emitEvent: false });
          itemControl.get('weight')?.disable({ emitEvent: false });
          itemControl.get('volumeM3')?.disable({ emitEvent: false });
          itemControl.get('dangerousGoodsClass')?.disable({ emitEvent: false });
        });
      });
    } else {
      this.getProductList().controls.forEach((control, index) => {
        control.get('category')?.enable({ emitEvent: false });
        this.getProductItems(index).controls.forEach(itemControl => {
          itemControl.get('description')?.enable({ emitEvent: false });
          itemControl.get('quantity')?.enable({ emitEvent: false });
          itemControl.get('weight')?.enable({ emitEvent: false });
          itemControl.get('volumeM3')?.enable({ emitEvent: false });
          itemControl.get('dangerousGoodsClass')?.enable({ emitEvent: false });
        });
      });
    }
  }

  async reloadProductCategories(clienteleId: number, isTimeBased: boolean) {
    const categoryPromise = this.productService.getProductCategories(clienteleId, 2, isTimeBased ?? false);
    const categories = await firstValueFrom(categoryPromise);
    this.setDefaultProductList();
    this.productCategories = categories;

    const dangerousGoodsClassPromise = this.productService.getDangerousGoodsCodes();
    const dangerousGoodsClass = await firstValueFrom(dangerousGoodsClassPromise);
    this.dangerousProductCodes = dangerousGoodsClass.sort((a, b) =>
      a.name.localeCompare(b.name)
    );
  }

  setDefaultProductList() {
    if (this.getProductList().length === 0) {
      this.getProductList().patchValue([]);
      this.addProduct();
    }
  }

  async initializeForm() {
    const isTimeBased = this.formGroup.get('jobInformation')?.get('timeBased')?.value;
    const clienteleId = this.formGroup.get('jobInformation')?.get('freightPayer')?.value;
    await this.reloadProductCategories(clienteleId, isTimeBased);
    if (this.startingProducts !== null) {
      const productInformationForm = this.formGroup.get('productInformation')?.get('products') as FormArray;
      productInformationForm.clear();
      this.startingProducts?.forEach((product: any, productIndex: number) => {
        productInformationForm.push(this.fb.group({
          jobProductId: this.fb.control(product.jobProductId),
          category: this.fb.control(product.productCategory, [Validators.required]),
          items: this.fb.array([])
        }));

        product.productItems.forEach((item: any, productItemIndex: number) => {
          const productForm = productInformationForm.at(productIndex).get('items') as FormArray;
          productForm.push(this.fb.group({
            jobProductItemId : item.jobProductItemId,
            description: item.itemDescription,
            quantity: item.quantity,
            weight: item.weight,
            volumeM3: item.volume,
            dangerousGoodsClass: item.dangerousGoodsClass
          }));
        });
      });
    } else {
      this.setDefaultProductList();
    }
  }

  getProductDetailForm() {
    return this.formGroup.get('productInformation') as FormGroup;
  }

  addItem(index: number) {
    this.getProductItems(index).push(this.fb.group({
      jobProductItemId: this.fb.control(0),
      description: this.fb.control("", [Validators.required]),
      quantity: this.fb.control(0, [atLeastOneRequiredValidator(['quantity', 'weight', 'volumeM3']), wholeNumberValidator()]),
      weight: this.fb.control(0, [atLeastOneRequiredValidator(['quantity', 'weight', 'volumeM3']), Validators.min(0)]),
      volumeM3: this.fb.control(0, [atLeastOneRequiredValidator(['quantity', 'weight', 'volumeM3']), Validators.min(0)]),
      dangerousGoodsClass: this.fb.control(null)
    }));
  }

  onProductCategoryChange($event: any, productLineIndex: number) {
    const productLine = this.getProductList().at(productLineIndex);
    productLine.patchValue({
      category: $event
    });
  }

  onDangerousGoodsClassChange($event: any, productIndex: number, productItemIndex: number) {
    const productItems = this.getProductItems(productIndex);
    const productItemLine = productItems.at(productItemIndex);
    productItemLine.patchValue({
      dangerousGoodsClass: $event,

    });
  }

  onProductItemLineChange(field: string, $event: any, productIndex: number, productItemIndex: number) {
    const productItems = this.getProductItems(productIndex);
    const productItemLine = productItems.at(productItemIndex);
    let updateModel: any = {};

    updateModel[field] = $event.target.value

    productItemLine.patchValue(updateModel);
  }

  addProduct() {
    this.getProductList().push(this.fb.group({
      jobProductId: this.fb.control(0),
      category: this.fb.control(null, [Validators.required]),
      items: this.fb.array([]),
    }));

    const index = this.getProductList().length - 1;
    this.addItem(index);
  }

  getProductItems(index: number) {
    const productList = this.getProductList().at(index)
    const items = productList.get('items') as FormArray;
    return items;
  }

  getProductList(): FormArray {
    const productInformationForm = this.formGroup.get('productInformation');
    if (productInformationForm) {
      return productInformationForm.get('products') as FormArray;
    } else {
      this.formGroup.addControl('productInformation', this.fb.group({
        products: this.fb.array([])
      }));
      return this.getProductList();
    }
  }

  removeItem(productIndex: number, itemIndex: number) {
    this.getProductItems(productIndex).removeAt(itemIndex);
  }

  removeProduct(index: number) {
    const newList = this.getProductList().value.map((product: any, i: number) => {
      if (i !== index) {
        return product;
      }
    }).filter((product: any) => product !== undefined);

    this.getProductList().clear();

    this.appRef.tick();

    newList.forEach((product: any) => {
      const itemList = product.items.map((item: any) => {
        return this.fb.group({
          jobProductItemId: item.jobProductItemId,
          description: item.description,
          quantity: item.quantity,
          weight: item.weight,
          volumeM3: item.volumeM3,
          dangerousGoodsClass: item.dangerousGoodsClass
        });
      });
      this.getProductList().push(this.fb.group({
        jobProductId: this.fb.control(product.jobProductId),
        category: this.fb.control(product.category, [Validators.required]),
        items: this.fb.array(itemList)
      }));
    });

  }
}
